import { Component, input } from '@angular/core';
import { type CellContext, injectFlexRenderContext } from '@tanstack/angular-table';
import { formatIsoDate } from '../../../lib/format-iso-date';
import { formatIsoDateTime } from '../../../lib/format-iso-datetime';
import type { ValueOf } from '../../../types';

/** Which Aurora date type the column holds — it decides the timezone frame. */
export const DateCellKind = {
  DATE: 'date',
  DATE_TIME: 'dateTime',
} as const;

export type DateCellKind = ValueOf<typeof DateCellKind>;

/**
 * Read-only cell that renders an Aurora date scalar.
 *
 * `kind` is not a formatting preference, it is the column's TYPE, and the two
 * types have genuinely different semantics:
 *
 * - `date` → `DD-MM-YYYY`, read in the UTC frame and never converted. A
 *   calendar day has no instant, so shifting it by the viewer's offset would
 *   move it to the neighbouring day.
 * - `dateTime` → `DD-MM-YYYY HH:mm`, converted to the viewer's own timezone. It
 *   IS an instant, and the server deliberately applies no timezone, so the
 *   conversion belongs here — only the browser knows the viewer's zone.
 *
 * NOT usable for `type: timestamp`, whose runtime value is an epoch NUMBER
 * rather than an ISO string.
 *
 * **Display only.** The CSV export reads the raw accessor value
 * (`Row.getValue`), never the rendered cell, so an exported date stays as
 * stored. Sorting and filtering likewise run server-side against the stored
 * value, so this cell cannot affect them.
 *
 * @example
 * cell: () => flexRenderComponent(DateCell, { inputs: { kind: 'dateTime' } }),
 */
@Component({
  selector: 'au-date-cell',
  template: `
    <span class="tabular-nums">{{ display }}</span>
  `,
})
export class DateCell<T = unknown> {
  /** The column's Aurora type. Defaults to `date`, the non-converting frame. */
  readonly kind = input<DateCellKind>(DateCellKind.DATE);

  private readonly context = injectFlexRenderContext<CellContext<T, string | null | undefined>>();

  /**
   * A getter, NOT a `computed()`. `injectFlexRenderContext()` returns a Proxy
   * whose `getValue()` is a plain method rather than a signal, so a `computed`
   * would track no dependency and cache the first value forever — and `kind`
   * never changes for a given column, so it would not save the computed either.
   * The getter re-runs on every render flexRender triggers.
   */
  protected get display(): string {
    const value = this.context.getValue();
    return this.kind() === DateCellKind.DATE_TIME ? formatIsoDateTime(value) : formatIsoDate(value);
  }
}
